import { useEffect, useState } from "react";
import { api, downloadFile } from "../lib/api";
import { PageHeader, PageSkeleton, KpiCard, ProgressBar, StatusBadge, Avatar, EmptyState } from "../components/fields";
import { FileDown, FileSpreadsheet, Users, ClipboardCheck, AlertTriangle, GraduationCap, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Reports() {
  const [compliance, setCompliance] = useState(null);
  const [students, setStudents] = useState([]);
  const [range, setRange] = useState({ start: "", end: "" });
  const [aides, setAides] = useState([]);
  const [daAide, setDaAide] = useState("");
  const [daWeek, setDaWeek] = useState("");
  const [daBusy, setDaBusy] = useState("");

  const weekOptions = () => {
    const opts = [];
    const d = new Date();
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    for (let i = 0; i < 4; i++) {
      const w = new Date(d);
      w.setDate(d.getDate() - i * 7);
      opts.push(w.toISOString().slice(0, 10));
    }
    return opts;
  };

  useEffect(() => {
    api.get("/admin/compliance").then((r) => setCompliance(r.data));
    api.get("/students").then((r) => setStudents(r.data));
    api.get("/aides").then((r) => { setAides(r.data); setDaAide(r.data[0]?.id || ""); }).catch(() => {});
  }, []);

  const exportF = async (studentId, name, type) => {
    const params = new URLSearchParams();
    if (range.start) params.set("start", range.start);
    if (range.end) params.set("end", range.end);
    const qs = params.toString() ? `?${params.toString()}` : "";
    try {
      await downloadFile(`/export/student/${studentId}/${type}${qs}`, `${name.replace(/\s+/g, "_").toLowerCase()}_support_record.${type === "csv" ? "csv" : "pdf"}`);
      toast.success(`${type.toUpperCase()} downloaded`);
    } catch { toast.error("Export failed"); }
  };

  const downloadDaReport = async (fmt) => {
    const week = daWeek || weekOptions()[0];
    const aide = aides.find((a) => a.id === daAide);
    setDaBusy(fmt);
    try {
      await downloadFile(`/aides/${daAide}/weekly-report?week_start=${week}&fmt=${fmt}`, `supportpro_hub_weekly_${(aide?.name || "aide").split(" ")[0].toLowerCase()}_${week}.${fmt}`);
      toast.success("Weekly report downloaded");
    } catch { toast.error("Export failed"); }
    setDaBusy("");
  };

  if (!compliance) return <PageSkeleton />;

  const scheduled = compliance.aide_stats.filter((a) => a.expected > 0);
  const totalExpected = scheduled.reduce((x, a) => x + a.expected, 0);
  const totalActual = scheduled.reduce((x, a) => x + a.actual, 0);
  const overallRate = totalExpected ? Math.round((totalActual / totalExpected) * 100) : null;
  const missing = Math.max(0, totalExpected - totalActual);

  const aideStatus = (a) => {
    if (a.expected === 0) return { badge: <StatusBadge tone="neutral">No scheduled blocks</StatusBadge>, tone: "green" };
    if (a.rate >= 90) return { badge: <StatusBadge tone="success">On Track</StatusBadge>, tone: "green" };
    if (a.rate >= 70) return { badge: <StatusBadge tone="warning">Needs Attention</StatusBadge>, tone: "amber" };
    return { badge: <StatusBadge tone="critical">Critical</StatusBadge>, tone: "red" };
  };

  const attention = [
    ...compliance.alerts.map((a) => ({ text: a.message, tone: "red" })),
    ...scheduled.filter((a) => a.rate < 70 && !compliance.alerts.some((al) => al.message.includes(a.name))).map((a) => ({ text: `${a.name} — ${a.rate}% documentation over the last 10 school days`, tone: "amber" })),
  ];

  return (
    <div className="space-y-5 stagger">
      <PageHeader title="Reports" subtitle="Documentation analytics and exportable student support records" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard testId="reports-kpi-rate" label="Documentation Rate" value={overallRate != null ? `${overallRate}%` : "—"} ring={overallRate ?? 0} sub="Last 10 school days" />
        <KpiCard testId="reports-kpi-aides" label="Active Aides" value={compliance.aide_stats.length} icon={Users} tone="blue" sub={`${scheduled.length} with schedules`} />
        <KpiCard testId="reports-kpi-students" label="Students Supported" value={students.length} icon={GraduationCap} tone="green" />
        <KpiCard testId="reports-kpi-missing" label="Missing Entries" value={missing} icon={missing > 0 ? AlertTriangle : ClipboardCheck} tone={missing > 0 ? "amber" : "green"} sub={missing > 0 ? "Across all aides" : "All caught up"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
        <div className="lg:col-span-2 bg-white border border-[#E5E9EE] rounded-2xl p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,31,56,0.04)]">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-5">
            <h3 className="font-heading text-[17px] font-semibold text-[#132238] tracking-tight">Aide Documentation</h3>
            <span className="text-xs font-medium text-[#667085] bg-[#F7F9FA] border border-[#E5E9EE] px-3 py-1.5 rounded-full">Last 10 School Days</span>
          </div>
          <div className="space-y-4">
            {compliance.aide_stats.map((a) => {
              const st = aideStatus(a);
              return (
                <div key={a.id} data-testid={`aide-doc-row-${a.id}`} className="flex items-center gap-3.5">
                  <Avatar name={a.name} tone="mint" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                      <p className="text-sm font-semibold text-[#132238]">{a.name}</p>
                      <div className="flex items-center gap-2.5">
                        {a.expected > 0 && <span className="text-xs text-[#667085]">{a.actual} / {a.expected}</span>}
                        {a.expected > 0 && <span className="text-sm font-bold text-[#132238]">{a.rate}%</span>}
                        {st.badge}
                      </div>
                    </div>
                    {a.expected > 0 ? (
                      <ProgressBar value={a.rate} tone={st.tone} height="h-1.5" />
                    ) : (
                      <p className="text-[11px] text-[#98A2B3]">No blocks scheduled during this period</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div data-testid="reports-alerts" className="bg-white border border-[#E5E9EE] rounded-2xl p-5 shadow-[0_1px_2px_rgba(15,31,56,0.04)]">
          <h3 className="font-heading text-[15px] font-semibold text-[#132238] flex items-center gap-2 mb-3">
            Needs Attention {attention.length > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{attention.length}</span>}
          </h3>
          {attention.length === 0 ? (
            <div className="flex items-center gap-2.5 text-sm text-[#667085]"><CheckCircle2 size={16} className="text-[#087F5B]" /> Everything is on track.</div>
          ) : (
            <div className="space-y-2">
              {attention.map((a, i) => (
                <div key={i} className={`rounded-xl border p-3 text-[13px] leading-relaxed ${a.tone === "red" ? "border-red-200 bg-red-50 text-red-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
                  {a.text}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {aides.length > 0 && (
        <div className="bg-white border border-[#E5E9EE] rounded-2xl p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,31,56,0.04)]">
          <h3 className="font-heading text-[17px] font-semibold text-[#132238] tracking-tight">Aide Weekly Reports</h3>
          <p className="text-[13px] text-[#667085] mt-1 mb-4">Download any aide's weekly summary — the same report aides export each Friday. Aides also receive theirs by email automatically every Friday afternoon.</p>
          <div className="flex flex-wrap items-center gap-2.5">
            <select data-testid="da-aide-select" value={daAide} onChange={(e) => setDaAide(e.target.value)}
              className="rounded-[10px] border border-[#E5E9EE] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#087F5B]/40">
              {aides.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <select data-testid="da-week-select" value={daWeek || weekOptions()[0]} onChange={(e) => setDaWeek(e.target.value)}
              className="rounded-[10px] border border-[#E5E9EE] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#087F5B]/40">
              {weekOptions().map((w, i) => <option key={w} value={w}>{`Week of ${w}${i === 0 ? " (this week)" : ""}`}</option>)}
            </select>
            <button data-testid="da-staff-pdf-btn" onClick={() => downloadDaReport("pdf")} disabled={!!daBusy || !daAide}
              className="flex items-center gap-1.5 text-xs font-semibold bg-[#087F5B] text-white hover:bg-[#066548] px-4 py-2 rounded-[10px] transition-colors duration-150 disabled:opacity-60">
              <FileDown size={13} /> {daBusy === "pdf" ? "Preparing…" : "PDF"}</button>
            <button data-testid="da-staff-csv-btn" onClick={() => downloadDaReport("csv")} disabled={!!daBusy || !daAide}
              className="flex items-center gap-1.5 text-xs font-semibold bg-white border border-[#E5E9EE] text-[#132238] hover:bg-slate-50 px-4 py-2 rounded-[10px] transition-colors duration-150 disabled:opacity-60">
              <FileSpreadsheet size={13} /> {daBusy === "csv" ? "Preparing…" : "CSV"}</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E5E9EE] rounded-2xl p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,31,56,0.04)]">
        <div className="flex items-start justify-between mb-1 gap-3 flex-wrap">
          <h3 className="font-heading text-[17px] font-semibold text-[#132238] tracking-tight">Student Record Exports</h3>
          <div className="flex items-center gap-1.5 text-xs text-[#667085]">
            <span className="font-semibold">Range</span>
            <input data-testid="reports-start-input" type="date" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
              className="rounded-lg border border-[#E5E9EE] px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#087F5B]/40" />
            <span>→</span>
            <input data-testid="reports-end-input" type="date" value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
              className="rounded-lg border border-[#E5E9EE] px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#087F5B]/40" />
          </div>
        </div>
        <p className="text-xs text-[#98A2B3] mb-4">Formatted for IEP meetings and school reporting — accepted without reformatting.</p>
        {students.length === 0 ? (
          <EmptyState icon={GraduationCap} title="No students yet" hint="Student support records appear here once students are added." />
        ) : (
          <div className="space-y-2">
            {students.map((s) => (
              <div key={s.id} data-testid={`report-student-${s.id}`} className="flex items-center justify-between gap-3 border border-[#E5E9EE] rounded-xl p-3.5 flex-wrap hover:border-slate-300 transition-colors duration-200">
                <div className="flex items-center gap-3">
                  <Avatar name={`${s.first_name} ${s.last_name}`} tone="mint" />
                  <div>
                    <p className="text-sm font-semibold text-[#132238]">{s.first_name} {s.last_name}</p>
                    <p className="text-xs text-[#667085]">Grade {s.grade} · Aide: {(s.aides || []).map((a) => a.name).join(", ") || "Unassigned"}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`report-pdf-btn-${s.id}`} onClick={() => exportF(s.id, `${s.first_name} ${s.last_name}`, "pdf")}
                    className="flex items-center gap-1.5 text-xs font-semibold bg-[#087F5B] text-white hover:bg-[#066548] px-3.5 py-2 rounded-[10px] transition-colors duration-150"><FileDown size={13} /> PDF</button>
                  <button data-testid={`report-csv-btn-${s.id}`} onClick={() => exportF(s.id, `${s.first_name} ${s.last_name}`, "csv")}
                    className="flex items-center gap-1.5 text-xs font-semibold bg-white border border-[#E5E9EE] text-[#132238] hover:bg-slate-50 px-3.5 py-2 rounded-[10px] transition-colors duration-150"><FileSpreadsheet size={13} /> CSV</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
